import { FC, useCallback, useContext, useEffect, useState } from 'react';
import * as types from '@models/types';
import * as url from '@api/requests';
import LoaderImage from '@assets/loader.gif';
import { ProfileContext } from '@providers/Profile';
import { SelectSingle } from '@components/Input/Select';
import CircleIcon from '@mui/icons-material/Circle';
import ExportButton from '@components/ExportButton';

type SupplyDemandSkillsProps = {
    countryData: types.CountryDataTypes,
    selectedProfile: types.SelectedProfileTypes
};

const SupplyDemandSkills: FC<SupplyDemandSkillsProps> = ({ countryData, selectedProfile }) => {
    const { universityProgramsData } = useContext(ProfileContext);

    const [universities, setUniversities] = useState<types.SelectedProfileChildrenTypes[]>([]);
    const [selectedUniversity, setSelectedUniversity] = useState<types.SelectedProfileChildrenTypes>({ id: "", title: "" });
    const [programs, setPrograms] = useState<types.SelectedProfileChildrenTypes[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<types.SelectedProfileChildrenTypes>({ id: "", title: "" });
    const [supplyDemandSkillsData, setSupplyDemandSkillsData] = useState<types.SupplyDemandSkillTypes>({});
    const [loadingSupplyDemandSkills, setLoadingSupplyDemandSkills] = useState<boolean>(false);

    useEffect(() => {
        if (universityProgramsData && universityProgramsData.length > 0 && universities.length === 0) {
            var tempUniversities: types.SelectedProfileChildrenTypes[] = universityProgramsData.map((value: types.UniversityProgramsTypes) => {
                return { id: value.universityId, title: value.universityName }
            });
            setUniversities(tempUniversities);
        }
    }, [universityProgramsData, universities]);

    const handleChangeUniversity = (event: any) => {
        if (!universities) return;

        if (event.target === "reset") {
            setSelectedUniversity({ id: "", title: "" });
            setPrograms([]);
            setSelectedProgram({ id: "", title: "" });
            setSupplyDemandSkillsData({});
        }
        else {
            const { value } = event.target
            const selectedUniversityObj = universities.length ? universities.find((e: any) => e.id === value) : null
            if (selectedUniversityObj)
                setSelectedUniversity(selectedUniversityObj);
        }
    }

    useEffect(() => {
        if (universityProgramsData && universityProgramsData.length > 0 && universities.length === 0) {
            var tempUniversities: types.SelectedProfileChildrenTypes[] = universityProgramsData.map((value: types.UniversityProgramsTypes) => {
                return { id: value.universityId, title: value.universityName }
            });
            setUniversities(tempUniversities);
        }
    }, [universityProgramsData, universities]);

    useEffect(() => {
        if (universityProgramsData && universityProgramsData.length > 0) {
            if (selectedUniversity && selectedUniversity.id) {
                var currentUniversity = universityProgramsData.find((value: types.UniversityProgramsTypes) => value.universityId === selectedUniversity.id);
                if (currentUniversity && currentUniversity.departments && currentUniversity.departments.length > 0) {
                    var tempPrograms: types.SelectedProfileChildrenTypes[] = [];
                    currentUniversity.departments.forEach((department: types.DepartmentTypes, departmentIndex: number) => {
                        tempPrograms.push({ parentId: "department-" + departmentIndex, parentTitle: department.departmentName, id: "", title: "" });
                        department.programs.forEach((program: types.ProgramTypes) => {
                            tempPrograms.push({ parentId: "department-" + departmentIndex, parentTitle: department.departmentName, id: program.programId, title: program.programName });
                        })
                    })
                    setPrograms(tempPrograms);
                }
            }
        }
    }, [selectedUniversity, universityProgramsData]);

    const handleChangeProgram = (event: any) => {
        if (!programs) return;

        if (event.target === "reset") {
            setSelectedProgram({ id: "", title: "" });
            setSupplyDemandSkillsData({});
        }
        else {
            const { value } = event.target
            const selectedProgramObj = programs.length ? programs.find((e: any) => e.id === value) : null
            if (selectedProgramObj && selectedProgramObj.id) {
                setSelectedProgram(selectedProgramObj);
                fetchSupplyDemandSkills(selectedProgramObj);
            }
        }
    }

    const fetchSupplyDemandSkills = useCallback(async (program: types.SelectedProfileChildrenTypes) => {
        setLoadingSupplyDemandSkills(true);
        const [errSupplyDemandSkills, incomingSupplyDemandSkills] = await url.getSupplyDemandSkills([countryData.hubId], selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [], program.id, (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [], selectedProfile.fromTimeline.title, selectedProfile.toTimeline.title, selectedProfile.isEntryLevel);
        if (errSupplyDemandSkills) console.warn(errSupplyDemandSkills);
        if (setSupplyDemandSkillsData) setSupplyDemandSkillsData(incomingSupplyDemandSkills)
        setLoadingSupplyDemandSkills(false);
    }, [countryData, selectedProfile.specializations, selectedProfile.provinces, selectedProfile.fromTimeline, selectedProfile.toTimeline, selectedProfile.isEntryLevel]);

    useEffect(() => {
        setSelectedUniversity({ id: "", title: "" });
        setPrograms([]);
        setSelectedProgram({ id: "", title: "" });
        setSupplyDemandSkillsData({});
    }, [countryData, selectedProfile.specializations, selectedProfile.provinces, selectedProfile.fromTimeline, selectedProfile.toTimeline, selectedProfile.isEntryLevel]);

    const getSkillColor = (ranking?: number) => {
        if (!ranking) return "circle-not-ranked";

        if (ranking >= 1 && ranking <= 100)
            return "circle-high";
        if (ranking >= 101 && ranking <= 200)
            return "circle-mid";
        if (ranking >= 201)
            return "circle-low";
    }


    return <>
        {!loadingSupplyDemandSkills ?
            <div>
                {supplyDemandSkillsData && ((supplyDemandSkillsData.demand && supplyDemandSkillsData.demand.length > 0) || (supplyDemandSkillsData.supply && supplyDemandSkillsData.supply.length > 0)) ?
                    <ExportButton countryName={countryData.country} exportDOMId="supplyDemandSkills-table" urlAPI={url.exportSupplyDemandSkills} urlDataAPI={{
                        hubIds: [countryData.hubId],
                        isced4Codes: selectedProfile.specializations.length ? selectedProfile.specializations.map((e: any) => e.id) : [],
                        programId: selectedProgram.id,
                        locations: (selectedProfile.provinces && selectedProfile.provinces.length) ? selectedProfile.provinces.map((e: any) => e.title) : [],
                        fromTimeline: selectedProfile.fromTimeline?.title,
                        toTimeline: selectedProfile.toTimeline?.title,
                        isEntryLevel: selectedProfile.isEntryLevel
                    }} />
                    : null}
                <div id="supplyDemandSkills-table">
                    <div className="supplyDemandDDLContainer">
                        <div className="supplyDemandDDL">
                            <SelectSingle
                                title="University"
                                name="universities"
                                selectedItem={selectedUniversity ? selectedUniversity.id : ""}
                                data={universities}
                                handleChange={handleChangeUniversity}
                            />
                        </div>
                        <div className="supplyDemandDDL">
                            <SelectSingle
                                title="Major"
                                name="majors"
                                selectedItem={selectedProgram ? selectedProgram.id : ""}
                                data={programs}
                                handleChange={handleChangeProgram}
                            />
                        </div>
                    </div>
                    {supplyDemandSkillsData && ((supplyDemandSkillsData.demand && supplyDemandSkillsData.demand.length > 0) || (supplyDemandSkillsData.supply && supplyDemandSkillsData.supply.length > 0)) ?
                        <>
                            <div id="legendSupplyDemandSkills" >
                                <div className="legend">
                                    <div className="circle-high"><CircleIcon /></div>
                                    <div className="title">#1 - #100</div>
                                </div>
                                <div className="legend">
                                    <div className="circle-mid"><CircleIcon /></div>
                                    <div className="title">#101 - #200</div>
                                </div>
                                <div className="legend">
                                    <div className="circle-low"><CircleIcon /></div>
                                    <div className="title">&gt;= #201</div>
                                </div>
                                <div className="legend">
                                    <div className="circle-not-ranked"><CircleIcon /></div>
                                    <div className="title">Not Ranked</div>
                                </div>
                                <div className="legend">
                                    <div className="circle-need-to-add"><CircleIcon /></div>
                                    <div className="title">Need to Add</div>
                                </div>
                            </div> 
                            <div id="supplyDemandSkills" >
                                <div className="supplySkills">
                                    <h6 className="border-bottom mb-2 pb-2"><strong>Top 25 Supply Skills</strong></h6>
                                    {supplyDemandSkillsData.supply && supplyDemandSkillsData.supply.length > 0 ?
                                        supplyDemandSkillsData.supply.map((supplySkill: types.SupplySkillTypes, index: number) => {
                                            return <div className="skill" key={"supply" + index}>
                                                <div className="name">{supplySkill.skill}</div>
                                                <div className={"ranking " + getSkillColor(supplySkill.skill_ranking)}><CircleIcon /></div>
                                            </div>
                                        })
                                        :
                                        null
                                    }
                                </div>
                                <div className="demandSkills">
                                    <h6 className="border-bottom mb-2 pb-2"><strong>Top 25 Demand Skills</strong></h6>
                                    {supplyDemandSkillsData.demand && supplyDemandSkillsData.demand.length > 0 ?
                                        supplyDemandSkillsData.demand.map((demandSkill: types.DemandSkillTypes, index: number) => {
                                            return <div className="skill" key={"demand" + index}>
                                                <div className="name">{demandSkill.skill}</div>
                                                <div className={"ranking " + (demandSkill.need_to_add ? "circle-need-to-add" : "")}>{demandSkill.need_to_add && <CircleIcon />}</div>
                                            </div>
                                        })
                                        :
                                        null
                                    }
                                </div>
                            </div>
                        </>
                    : <div className="na">N/A</div>}
                </div>
            </div>
            :
            <div id="supply-demand-skills-loading"><img src={LoaderImage} alt="" className="mx-auto d-block" style={{ width: 30 }} /></div>
        }
    </>
};

export default SupplyDemandSkills;